import React, {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {FieldsErrorResponse} from '@chekin/api';
import {getErrorMessage, isObject, STATUSES} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics, IV_ERROR_CATEGORY} from '../../../analytics';
import {CHECK_TIMED_OUT_REASON, SERVICE_UNAVAILABLE_REASON} from '../serviceFailure';
import type {IVFailureDetails, IVFailureReason} from './types';

const REJECTED_UPLOAD_STATUS_CODES = new Set([400, 413, 415, 422]);

type UseIVProcessStateProps = {
  isHardwareScanning?: boolean;
  onError?: (error: Error | string) => void;
  resetScroll?: () => void;
};

function useIVProcessState({
  isHardwareScanning,
  onError,
  resetScroll,
}: UseIVProcessStateProps) {
  const {t} = useTranslation('identity-verification');
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [errorDescription, setErrorDescription] = React.useState<string>();
  const [errorReason, setErrorReason] = React.useState<IVFailureReason>();
  const [failureCount, setFailureCount] = React.useState(0);
  const lastFailureRef = React.useRef<{reason?: IVFailureReason} | null>(null);
  const [processState, setProcessState] = React.useState<STATUSES>(STATUSES.loading);
  const [isProcessModalOpen, setIsProcessModalOpen] = React.useState(false);

  const openProcessModal = useCallback(() => {
    setIsProcessModalOpen(true);
  }, []);

  const closeProcessModal = useCallback(() => {
    setIsProcessModalOpen(false);
  }, []);

  useEffect(() => {
    if (isHardwareScanning) {
      openProcessModal();
      return;
    }

    closeProcessModal();
  }, [closeProcessModal, isHardwareScanning, openProcessModal]);

  const closeAndCleanLoadingModal = useCallback(() => {
    closeProcessModal();
    setProcessState(STATUSES.idle);
  }, [closeProcessModal]);

  useEffect(() => {
    return () => {
      closeAndCleanLoadingModal();
      resetScroll?.();
    };
  }, [closeAndCleanLoadingModal, resetScroll]);

  const onBefore = useCallback(() => {
    setProcessState(STATUSES.loading);
    openProcessModal();
  }, [openProcessModal]);

  const setFailureState = useCallback(
    (nextErrorMessage?: string, details?: IVFailureDetails) => {
      setErrorMessage(nextErrorMessage);
      setErrorDescription(details?.description);
      setErrorReason(details?.reason);
      const isSameFailure = lastFailureRef.current?.reason === details?.reason;
      lastFailureRef.current = {reason: details?.reason};
      setFailureCount(count => (isSameFailure ? count + 1 : 1));
      setProcessState(STATUSES.error);
    },
    [],
  );

  const handleError = useCallback(
    (error: Error | FieldsErrorResponse) => {
      const statusCode =
        isObject(error) && 'status_code' in error
          ? (error as FieldsErrorResponse).status_code
          : undefined;
      const category =
        statusCode == null ? IV_ERROR_CATEGORY.network : IV_ERROR_CATEGORY.server;

      Analytics.track(ANALYTIC_EVENTS.ivError, {
        'Error message': getErrorMessage(error),
        error_category: category,
        status_code: statusCode ?? null,
      });
      if (statusCode != null && REJECTED_UPLOAD_STATUS_CODES.has(statusCode)) {
        setFailureState();
      } else {
        setFailureState(t('service_unavailable_title'), {
          description: t('service_unavailable_text'),
          reason: SERVICE_UNAVAILABLE_REASON,
        });
      }
      if (isObject(error) && 'errors' in error) {
        onError?.(error.errors?.[0]?.message);
        return;
      }

      onError?.(error as Error);
    },
    [onError, setFailureState, t],
  );

  const handleCheckTimeout = useCallback(() => {
    setFailureState(t('check_timed_out_title'), {
      description: t('check_timed_out_text'),
      reason: CHECK_TIMED_OUT_REASON,
    });
  }, [setFailureState, t]);

  return {
    errorMessage,
    errorDescription,
    errorReason,
    failureCount,
    processState,
    isProcessModalOpen,
    onBefore,
    closeAndCleanLoadingModal,
    handleError,
    setFailureState,
    handleCheckTimeout,
    setProcessState,
  };
}

export {useIVProcessState};
